import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Plus, Trash2, Save, LogOut, Trophy, RefreshCw, ExternalLink, Edit3, X, Check } from 'lucide-react';
import {
  listChampionships,
  updateChampionshipMeta,
  addChampionshipPlayer,
  editChampionshipPlayer,
  deleteChampionshipPlayer,
  addChampionshipCoach,
  editChampionshipCoach,
  deleteChampionshipCoach,
  getToken,
  clearToken,
  me,
} from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

export default function AdminChampions() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [teams, setTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState('2013-peewee-sb');
  const [authOk, setAuthOk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPlayer, setNewPlayer] = useState({ name: '', jersey: '', position: '' });
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({ name: '', jersey: '', position: '' });
  const [newCoach, setNewCoach] = useState({ name: '', role: 'Head Coach', img: '' });
  const [editingCoachId, setEditingCoachId] = useState(null);
  const [editingCoachDraft, setEditingCoachDraft] = useState({ name: '', role: '', img: '' });
  const [metaDraft, setMetaDraft] = useState(null); // set when editing team meta
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listChampionships();
      setTeams(data);
      if (data.length) {
        setActiveTeamId((current) => data.some((team) => team.teamId === current) ? current : data[0].teamId);
      }
    } catch (e) {
      toast({ title: 'Error loading championships', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  if (authOk === false) return <Navigate to="/admin/login" replace />;
  if (authOk === null) return null;

  const activeTeam = teams.find((t) => t.teamId === activeTeamId) || teams[0];

  const addPlayer = async (e) => {
    e.preventDefault();
    if (!newPlayer.name.trim()) {
      toast({ title: 'Name required' });
      return;
    }
    try {
      const updated = await addChampionshipPlayer(activeTeamId, newPlayer);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      setNewPlayer({ name: '', jersey: '', position: '' });
      toast({ title: 'Player added' });
    } catch (err) {
      toast({ title: 'Add failed', description: err.message });
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditingDraft({ name: p.name, jersey: p.jersey || '', position: p.position || '' });
  };

  const saveEdit = async () => {
    if (!editingDraft.name.trim()) return;
    try {
      const updated = await editChampionshipPlayer(activeTeamId, editingId, editingDraft);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      setEditingId(null);
      toast({ title: 'Player updated' });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message });
    }
  };

  const removePlayer = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try {
      const updated = await deleteChampionshipPlayer(activeTeamId, id);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      if (editingId === id) setEditingId(null);
      toast({ title: 'Player removed' });
    } catch (err) {
      toast({ title: 'Remove failed', description: err.message });
    }
  };

  const addCoach = async (e) => {
    e.preventDefault();
    if (!newCoach.name.trim()) { toast({ title: 'Coach name required' }); return; }
    try {
      const updated = await addChampionshipCoach(activeTeamId, newCoach);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      setNewCoach({ name: '', role: 'Head Coach', img: '' });
      toast({ title: 'Coach added' });
    } catch (err) {
      toast({ title: 'Add failed', description: err.message });
    }
  };

  const startCoachEdit = (c) => {
    setEditingCoachId(c.id);
    setEditingCoachDraft({ name: c.name, role: c.role || 'Coach', img: c.img || '' });
  };

  const saveCoachEdit = async () => {
    if (!editingCoachDraft.name.trim()) return;
    try {
      const updated = await editChampionshipCoach(activeTeamId, editingCoachId, editingCoachDraft);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      setEditingCoachId(null);
      toast({ title: 'Coach updated' });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message });
    }
  };

  const removeCoach = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try {
      const updated = await deleteChampionshipCoach(activeTeamId, id);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      if (editingCoachId === id) setEditingCoachId(null);
      toast({ title: 'Coach removed' });
    } catch (err) {
      toast({ title: 'Remove failed', description: err.message });
    }
  };

  const startMetaEdit = () => {
    setMetaDraft({
      title: activeTeam.title || '',
      year: activeTeam.year || '',
      division: activeTeam.division || '',
      event: activeTeam.event || '',
      dates: activeTeam.dates || '',
      location: activeTeam.location || '',
      img: activeTeam.img || '',
    });
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const payload = { ...metaDraft, year: metaDraft.year ? Number(metaDraft.year) : undefined };
      const updated = await updateChampionshipMeta(activeTeamId, payload);
      setTeams((prev) => prev.map((t) => (t.teamId === activeTeamId ? updated : t)));
      setMetaDraft(null);
      toast({ title: 'Team details saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message });
    } finally {
      setSavingMeta(false);
    }
  };

  const logout = () => { clearToken(); nav('/admin/login'); };

  return (
    <div className="min-h-screen bg-[#f5f6f8] font-body">
      <div className="bg-[#0a1f44] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto object-contain" />
            <div>
              <div className="font-display text-xl tracking-wide">ADMIN CMS</div>
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Championship Rosters</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/alumni#championships" target="_blank" data-testid="view-live-btn" className="hidden md:inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-white/70 hover:text-white">
              <ExternalLink className="w-4 h-4" /> View Live
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {loading || !activeTeam ? (
          <div className="text-center py-16 text-[#0a1f44]/50">Loading…</div>
        ) : (
          <>
            {/* Team selector */}
            {teams.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="champ-team-selector">
                {teams.map((t) => (
                  <button
                    key={t.teamId}
                    onClick={() => setActiveTeamId(t.teamId)}
                    data-testid={`champ-tab-${t.teamId}`}
                    className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 ${activeTeamId === t.teamId ? 'bg-[#0a1f44] text-white border-[#0a1f44]' : 'bg-white text-[#0a1f44] border-[#0a1f44]/20 hover:border-[#c8102e]'}`}
                  >
                    {t.year} · {t.division}
                  </button>
                ))}
              </div>
            )}

            {/* Team meta card */}
            <div className="bg-white shadow-sm p-6" data-testid="champ-meta-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-[#c8102e]" />
                  <div>
                    <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Championship</div>
                    <div className="font-display text-2xl text-[#0a1f44] leading-tight">{activeTeam.title}</div>
                  </div>
                </div>
                {metaDraft ? (
                  <div className="flex gap-2">
                    <button onClick={saveMeta} disabled={savingMeta} data-testid="champ-meta-save" className="inline-flex items-center gap-1 bg-[#c8102e] text-white text-xs font-heading uppercase tracking-widest px-3 py-2 disabled:opacity-50">
                      <Save className="w-3 h-3" /> {savingMeta ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setMetaDraft(null)} data-testid="champ-meta-cancel" className="inline-flex items-center gap-1 border border-[#0a1f44]/30 text-[#0a1f44] text-xs font-heading uppercase tracking-widest px-3 py-2">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={startMetaEdit} data-testid="champ-meta-edit" className="inline-flex items-center gap-1 border border-[#0a1f44]/30 text-[#0a1f44] hover:border-[#c8102e] text-xs font-heading uppercase tracking-widest px-3 py-2">
                    <Edit3 className="w-3 h-3" /> Edit Details
                  </button>
                )}
              </div>
              {metaDraft ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <MetaField label="Title" value={metaDraft.title} onChange={(v) => setMetaDraft({ ...metaDraft, title: v })} testid="champ-meta-title" />
                  <MetaField label="Year" value={metaDraft.year} onChange={(v) => setMetaDraft({ ...metaDraft, year: v })} testid="champ-meta-year" type="number" />
                  <MetaField label="Division" value={metaDraft.division} onChange={(v) => setMetaDraft({ ...metaDraft, division: v })} testid="champ-meta-division" />
                  <MetaField label="Event" value={metaDraft.event} onChange={(v) => setMetaDraft({ ...metaDraft, event: v })} testid="champ-meta-event" />
                  <MetaField label="Dates" value={metaDraft.dates} onChange={(v) => setMetaDraft({ ...metaDraft, dates: v })} testid="champ-meta-dates" />
                  <MetaField label="Location" value={metaDraft.location} onChange={(v) => setMetaDraft({ ...metaDraft, location: v })} testid="champ-meta-location" />
                  <div className="md:col-span-2">
                    <MetaField label="Photo URL" value={metaDraft.img} onChange={(v) => setMetaDraft({ ...metaDraft, img: v })} testid="champ-meta-img" />
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm text-[#0a1f44]/80">
                  <MetaRead label="Year" value={activeTeam.year} />
                  <MetaRead label="Division" value={activeTeam.division} />
                  <MetaRead label="Event" value={activeTeam.event} />
                  <MetaRead label="Dates" value={activeTeam.dates} />
                  <MetaRead label="Location" value={activeTeam.location} />
                  <MetaRead label="Players" value={activeTeam.players.length} />
                </div>
              )}
            </div>

            {/* Coaching Staff */}
            <div className="bg-white shadow-sm p-6" data-testid="champ-coaches-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-display text-2xl text-[#0a1f44]">COACHING STAFF</h2>
                  <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">{(activeTeam.coaches || []).length} coach{(activeTeam.coaches || []).length !== 1 ? 'es' : ''}</div>
                </div>
              </div>

              <form onSubmit={addCoach} className="grid md:grid-cols-[1fr_160px_1fr_auto] gap-2 mb-6" data-testid="champ-add-coach-row">
                <input type="text" placeholder="Coach name *" value={newCoach.name} onChange={(e) => setNewCoach({ ...newCoach, name: e.target.value })} data-testid="champ-add-coach-name" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
                <select value={newCoach.role} onChange={(e) => setNewCoach({ ...newCoach, role: e.target.value })} data-testid="champ-add-coach-role" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e] bg-white">
                  <option>Head Coach</option>
                  <option>Assistant Coach</option>
                  <option>Offensive Coordinator</option>
                  <option>Defensive Coordinator</option>
                  <option>Team Mom</option>
                  <option>Trainer</option>
                </select>
                <input type="text" placeholder="Photo URL (optional)" value={newCoach.img} onChange={(e) => setNewCoach({ ...newCoach, img: e.target.value })} data-testid="champ-add-coach-img" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
                <button type="submit" data-testid="champ-add-coach-btn" className="inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-2.5">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </form>

              {(activeTeam.coaches || []).length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-[#0a1f44]/15 text-[#0a1f44]/60 text-sm" data-testid="champ-coaches-empty">
                  No coaches yet. Add the head coach and assistants above.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="champ-coaches-list">
                  {(activeTeam.coaches || []).map((c) => (
                    <div key={c.id} data-testid={`champ-coach-row-${c.id}`} className="flex items-center gap-3 border border-[#0a1f44]/10 p-3">
                      <div className="w-14 h-14 bg-[#0a1f44] flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {c.img ? <img src={c.img} alt={c.name} className="w-full h-full object-cover" /> : <Trophy className="w-6 h-6 text-white/40" />}
                      </div>
                      {editingCoachId === c.id ? (
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <input value={editingCoachDraft.name} onChange={(e) => setEditingCoachDraft({ ...editingCoachDraft, name: e.target.value })} data-testid="champ-edit-coach-name" className="col-span-2 border border-[#c8102e] px-2 py-1 text-sm" />
                          <input value={editingCoachDraft.role} onChange={(e) => setEditingCoachDraft({ ...editingCoachDraft, role: e.target.value })} data-testid="champ-edit-coach-role" className="border border-[#c8102e] px-2 py-1 text-xs" placeholder="Role" />
                          <input value={editingCoachDraft.img} onChange={(e) => setEditingCoachDraft({ ...editingCoachDraft, img: e.target.value })} data-testid="champ-edit-coach-img" className="border border-[#c8102e] px-2 py-1 text-xs" placeholder="Photo URL" />
                          <div className="col-span-2 flex gap-1">
                            <button onClick={saveCoachEdit} data-testid={`champ-save-coach-${c.id}`} className="p-1.5 bg-[#c8102e] text-white text-xs"><Check className="w-3 h-3" /></button>
                            <button onClick={() => setEditingCoachId(null)} data-testid={`champ-cancel-coach-${c.id}`} className="p-1.5 border border-[#0a1f44]/30 text-xs"><X className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-lg text-[#0a1f44] truncate">{c.name}</div>
                            <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">{c.role || 'Coach'}</div>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => startCoachEdit(c)} data-testid={`champ-edit-coach-btn-${c.id}`} className="p-1.5 border border-[#0a1f44]/30 hover:border-[#c8102e]"><Edit3 className="w-3.5 h-3.5 text-[#0a1f44]" /></button>
                            <button onClick={() => removeCoach(c.id, c.name)} data-testid={`champ-delete-coach-btn-${c.id}`} className="p-1.5 border border-[#c8102e] text-[#c8102e] hover:bg-[#c8102e] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Roster grid */}
            <div className="bg-white shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-display text-2xl text-[#0a1f44]">CHAMPIONSHIP ROSTER</h2>
                  <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">{activeTeam.players.length} player{activeTeam.players.length !== 1 ? 's' : ''}</div>
                </div>
                <button onClick={load} className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reload</button>
              </div>

              {/* Add-player row */}
              <form onSubmit={addPlayer} className="grid md:grid-cols-[1fr_120px_140px_auto] gap-2 mb-6" data-testid="champ-add-player-row">
                <input
                  type="text"
                  placeholder="Player name *"
                  value={newPlayer.name}
                  onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                  data-testid="champ-add-name"
                  className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]"
                />
                <input
                  type="text"
                  placeholder="Jersey #"
                  value={newPlayer.jersey}
                  onChange={(e) => setNewPlayer({ ...newPlayer, jersey: e.target.value })}
                  data-testid="champ-add-jersey"
                  className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]"
                />
                <input
                  type="text"
                  placeholder="Position"
                  value={newPlayer.position}
                  onChange={(e) => setNewPlayer({ ...newPlayer, position: e.target.value })}
                  data-testid="champ-add-position"
                  className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]"
                />
                <button type="submit" data-testid="champ-add-btn" className="inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-2.5">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </form>

              {/* Players table */}
              {activeTeam.players.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-[#0a1f44]/15 text-[#0a1f44]/60" data-testid="champ-empty-state">
                  No players yet. Use the form above to add the first name.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="champ-players-table">
                    <thead>
                      <tr className="text-left border-b-2 border-[#0a1f44]/10">
                        <th className="py-2 pr-3 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/70 w-14">#</th>
                        <th className="py-2 pr-3 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/70">Player</th>
                        <th className="py-2 pr-3 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/70 w-24">Jersey</th>
                        <th className="py-2 pr-3 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/70 w-40">Position</th>
                        <th className="py-2 pr-3 w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeTeam.players.map((p, idx) => (
                        <tr key={p.id} data-testid={`champ-row-${p.id}`} className="border-b border-[#0a1f44]/5">
                          <td className="py-2 pr-3 text-[#0a1f44]/50 font-mono">{idx + 1}</td>
                          {editingId === p.id ? (
                            <>
                              <td className="py-2 pr-3">
                                <input type="text" value={editingDraft.name} onChange={(e) => setEditingDraft({ ...editingDraft, name: e.target.value })} data-testid="champ-edit-name" className="w-full border border-[#c8102e] px-2 py-1" />
                              </td>
                              <td className="py-2 pr-3">
                                <input type="text" value={editingDraft.jersey} onChange={(e) => setEditingDraft({ ...editingDraft, jersey: e.target.value })} data-testid="champ-edit-jersey" className="w-full border border-[#c8102e] px-2 py-1" />
                              </td>
                              <td className="py-2 pr-3">
                                <input type="text" value={editingDraft.position} onChange={(e) => setEditingDraft({ ...editingDraft, position: e.target.value })} data-testid="champ-edit-position" className="w-full border border-[#c8102e] px-2 py-1" />
                              </td>
                              <td className="py-2 pr-3">
                                <div className="flex gap-1">
                                  <button onClick={saveEdit} data-testid={`champ-save-${p.id}`} className="p-1.5 bg-[#c8102e] text-white" title="Save"><Check className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => setEditingId(null)} data-testid={`champ-cancel-${p.id}`} className="p-1.5 border border-[#0a1f44]/30" title="Cancel"><X className="w-3.5 h-3.5" /></button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-2 pr-3 text-[#0a1f44] font-medium">{p.name}</td>
                              <td className="py-2 pr-3 text-[#0a1f44]/70 font-mono">{p.jersey || '—'}</td>
                              <td className="py-2 pr-3 text-[#0a1f44]/70">{p.position || '—'}</td>
                              <td className="py-2 pr-3">
                                <div className="flex gap-1">
                                  <button onClick={() => startEdit(p)} data-testid={`champ-edit-btn-${p.id}`} className="p-1.5 border border-[#0a1f44]/30 hover:border-[#c8102e]" title="Edit"><Edit3 className="w-3.5 h-3.5 text-[#0a1f44]" /></button>
                                  <button onClick={() => removePlayer(p.id, p.name)} data-testid={`champ-delete-btn-${p.id}`} className="p-1.5 border border-[#c8102e] text-[#c8102e] hover:bg-[#c8102e] hover:text-white" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MetaRead({ label, value }) {
  return (
    <div>
      <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 mb-0.5">{label}</div>
      <div className="text-[#0a1f44] font-medium">{value || '—'}</div>
    </div>
  );
}

function MetaField({ label, value, onChange, testid, type = 'text' }) {
  return (
    <label className="block">
      <span className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]"
      />
    </label>
  );
}
